export type EngineeringTeam = {
  name: string;
  key: string;
  datadog: {
    teamTag: string;
  };
  playwright: {
    tag: string;
  };
  slack: {
    userGroupId: string;
    userGroupHandle: string;
  };
};

/** Registry of engineering teams and their identifiers across systems. */
export const engineeringTeams = {
  /**
   * All engineers on Team AI Agents
   *
   * *Slack:* {@link https://launchdarkly.slack.com/archives/C07GGGLCGQG | #team-ai-agents} */
  TEAM_AI_AGENTS: {
    name: 'Team AI Agents',
    key: 'team-ai-agents',
    datadog: {
      teamTag: 'team-ai-agents',
    },
    playwright: {
      tag: '@team-ai-agents',
    },
    slack: {
      userGroupId: 'S0BQXEXLUEQ',
      userGroupHandle: '@ai-agents-oncall',
    },
  },
  /**
   * All engineers on Team AI Evals
   */
  TEAM_AI_EVALS: {
    name: 'Team AI Evals',
    key: 'team-ai-evals',
    datadog: {
      teamTag: 'team-ai-evals',
    },
    playwright: {
      tag: '@team-ai-evals',
    },
    slack: {
      userGroupId: 'S0BQMHH51L3',
      userGroupHandle: '@ai-evals-oncall',
    },
  },
  /**
   * All engineers on Team Billing
   *
   * *Slack:* {@link https://launchdarkly.slack.com/archives/CUEN57YTA | #ask-billing-and-internal-systems} */
  TEAM_BILLING: {
    name: 'Team Billing',
    key: 'team-billing',
    datadog: {
      teamTag: 'team-billing',
    },
    playwright: {
      tag: '@team-billing',
    },
    slack: {
      userGroupId: 'S07QVPCS21X',
      userGroupHandle: '@billing-internal-systems-team',
    },
  },
  /**
   * All engineers on Team Enterprise
   *
   * *Slack:* {@link https://launchdarkly.slack.com/archives/C09N9RR6A7N | #team-enterprise} */
  TEAM_ENTERPRISE: {
    name: 'Team Enterprise',
    key: 'team-enterprise',
    datadog: {
      teamTag: 'team-fm-enterprise',
    },
    playwright: {
      tag: '@team-enterprise',
    },
    slack: {
      userGroupId: 'S0AHTT4DMLZ',
      userGroupHandle: '@team-enterprise-engineers',
    },
  },
  /**
   * All engineers on Team Experimentation
   *
   * *Slack:* {@link https://launchdarkly.slack.com/archives/C02MDJRGLMU | #team-experimentation} */
  TEAM_EXPERIMENTATION: {
    name: 'Team Experimentation',
    key: 'team-experimentation',
    datadog: {
      teamTag: 'team-experimentation',
    },
    playwright: {
      tag: '@team-experimentation',
    },
    slack: {
      userGroupId: 'S06UR1TDU3T',
      userGroupHandle: '@experimentation-oncall',
    },
  },
  /**
   * All engineers on Team Foundation
   *
   * *Slack:* {@link https://launchdarkly.slack.com/archives/C0ACDTU2E4D | #team-foundation} */
  TEAM_FOUNDATION: {
    name: 'Team Foundation',
    key: 'team-foundation',
    datadog: {
      teamTag: 'team-foundation',
    },
    playwright: {
      tag: '@team-foundation',
    },
    slack: {
      userGroupId: 'S0AHTSQJVMF',
      userGroupHandle: '@team-foundation-engineers',
    },
  },
  /**
   * All engineers on Team Agent Platform, formerly Team Metrics
   *
   * *Slack:* {@link https://launchdarkly.slack.com/archives/C07BPTRL0UT | #team-agent-platform} */
  TEAM_AGENT_PLATFORM: {
    name: 'Team Agent Platform',
    key: 'team-agent-platform',
    datadog: {
      teamTag: 'team-agent-platform',
    },
    playwright: {
      tag: '@team-agent-platform',
    },
    slack: {
      userGroupId: 'S095RCTN0F4',
      userGroupHandle: '@metrics-oncall',
    },
  },
  /**
   * All engineers on Team Release Guardian
   *
   * *Slack:* {@link https://launchdarkly.slack.com/archives/C012PMFLK8T | #team-guardian-releases} */
  TEAM_RELEASE_GUARDIAN: {
    name: 'Team Release Guardian',
    key: 'team-guardian-releases',
    datadog: {
      teamTag: 'team-guardian-releases',
    },
    playwright: {
      tag: '@team-guardian-releases',
    },
    slack: {
      userGroupId: 'S08AXCU16BT',
      userGroupHandle: '@rg-on-call-engineer',
    },
  },
  /**
   * All engineers on Team Cloud Engineering
   */
  TEAM_CLOUD_ENGINEERING: {
    name: 'Team Cloud Engineering',
    key: 'team-cloud-engineering',
    datadog: {
      teamTag: 'team-cloud-engineering',
    },
    playwright: {
      tag: '@team-cloud-engineering',
    },
    slack: {
      userGroupId: 'S0APHC7G0UX',
      userGroupHandle: '@cloud-engineering-on-call',
    },
  },
  /**
   * All engineers on Team Flag Delivery
   */
  TEAM_FLAG_DELIVERY: {
    name: 'Team Flag Delivery',
    key: 'team-flag-delivery',
    datadog: {
      teamTag: 'team-flag-delivery',
    },
    playwright: {
      tag: '@team-flag-delivery',
    },
    slack: {
      userGroupId: 'S0AQ7T4R9UH',
      userGroupHandle: '@flag-delivery-on-call',
    },
  },
  /**
   * All engineers on Team Guardian Monitoring
   */
  TEAM_GUARDIAN_MONITORING: {
    name: 'Team Guardian Monitoring',
    key: 'team-guardian-monitoring',
    datadog: {
      teamTag: 'team-guardian-monitoring',
    },
    playwright: {
      tag: '@team-guardian-monitoring',
    },
    slack: {
      userGroupId: 'S096FK0KQMT',
      userGroupHandle: '@gm-on-call-engineer',
    },
  },
  /**
   * All engineers on Team Product Security
   */
  TEAM_PRODUCT_SECURITY: {
    name: 'Team Product Security',
    key: 'team-product-security',
    datadog: {
      teamTag: 'team-product-security',
    },
    playwright: {
      tag: '@team-product-security',
    },
    slack: {
      userGroupId: 'S0AQSALNZLN',
      userGroupHandle: '@product-security-oncall',
    },
  },
  /**
   * All engineers on Team Service Frameworks
   */
  TEAM_SERVICE_FRAMEWORKS: {
    name: 'Team Service Frameworks',
    key: 'team-service-frameworks',
    datadog: {
      teamTag: 'team-service-frameworks',
    },
    playwright: {
      tag: '@team-service-frameworks',
    },
    slack: {
      userGroupId: 'S0AQXQVU3LL',
      userGroupHandle: '@service-frameworks-on-call',
    },
  },
  /**
   * All engineers on Team Warehouse Integrations
   */
  TEAM_WAREHOUSE_INTEGRATIONS: {
    name: 'Team Warehouse Integrations',
    key: 'team-warehouse-integrations',
    datadog: {
      teamTag: 'team-warehouse-integrations',
    },
    playwright: {
      tag: '@team-warehouse-integrations',
    },
    slack: {
      userGroupId: 'S0A9K9HHW4S',
      userGroupHandle: '@wit-oncall',
    },
  },
  /**
   * All engineers on Team Observability
   *
   * *Slack:* {@link https://launchdarkly.slack.com/archives/S08M6CK16RZ | #o11y-team} */
  TEAM_O11Y: {
    name: 'Team Observability',
    key: 'team-o11y',
    datadog: {
      teamTag: 'team-o11y',
    },
    playwright: {
      tag: '@team-o11y',
    },
    slack: {
      userGroupId: 'S08M6CK16RZ',
      userGroupHandle: '@o11y-team',
    },
  },
} as const satisfies Record<`TEAM_${string}`, EngineeringTeam>;

export type EngineeringTeamKey = (typeof engineeringTeams)[keyof typeof engineeringTeams]['key'];
